import React from "react";

/**
 * Lightweight linkifier. Converts any "https://…" or "http://…" substring
 * inside a text block into a clickable external link with an interstitial
 * confirm (members shouldn't accidentally land on an external site).
 *
 * Renders the result as a single <span> so it can be dropped wherever the
 * codebase used to render `whitespace-pre-wrap` plain text.
 */
const URL_RE = /(https?:\/\/[^\s<>"')]+)/gi;

function externalDomain(href) {
  try { return new URL(href).hostname.replace(/^www\./, ""); }
  catch { return href; }
}

function ExternalLink({ href, children }) {
  const onClick = (e) => {
    const domain = externalDomain(href);
    const ok = window.confirm(
      `Leaving Jesus Just Us — continue to ${domain}?\n\nWe don't control external sites. Make sure the content honors our community covenant.`
    );
    if (!ok) e.preventDefault();
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="text-burgundy underline decoration-burgundy/40 underline-offset-2 hover:text-gold hover:decoration-gold transition-colors break-words"
      data-testid="external-link"
    >
      {children}
    </a>
  );
}

export default function Linkify({ children, className = "" }) {
  const text = typeof children === "string" ? children : String(children ?? "");
  if (!text) return null;

  const parts = [];
  let last = 0;
  let m;
  // Reset regex state because /g regexes are stateful across calls.
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    const start = m.index;
    if (start > last) parts.push(text.slice(last, start));
    const url = m[1];
    parts.push(
      <ExternalLink key={`${start}-${url}`} href={url}>
        {url.replace(/^https?:\/\//i, "")}
      </ExternalLink>
    );
    last = start + url.length;
  }
  if (last < text.length) parts.push(text.slice(last));

  return (
    <span className={`whitespace-pre-wrap ${className}`}>
      {parts.length ? parts : text}
    </span>
  );
}
